import { useEffect, useState } from 'react'

// Does the browser have a session, as far as the shell needs to know.
//
// 🔴 THIS IS NOT A SECURITY BOUNDARY, and the comment is here so nobody later
// mistakes it for one. The session cookie is HttpOnly (ADR-0003 D2), so no
// JavaScript on this page can read it — this hook asks a Function instead, and
// what it gets back decides whether the top bar says "Sign in" or "Sign out".
// That is chrome.
//
// The real guard is server-side, in site/functions/_middleware.js: a signed-out
// browser asking for a tier-3 document is redirected to sign in and never
// receives the page. This hook cannot grant anything the middleware refused,
// and nothing on a tier-3 page comes from the bundle — it comes from a Function
// that establishes the caller itself (D3). So the worst this can do is show the
// wrong button.
//
// ⚠️ It therefore fails CLOSED on purpose: any error — offline, 500, no
// Functions at all in `vite dev` — resolves to signed OUT. The only direction a
// failure can move the UI is towards showing less.

export const SESSION_ENDPOINT = '/api/session'

/** loading -> the answer has not arrived; render neither state as fact. */
export const LOADING = 'loading'
export const SIGNED_IN = 'signed-in'
export const SIGNED_OUT = 'signed-out'

/**
 * Where "sign in" goes, from wherever it was pressed.
 *
 * 🔴 ONE OWNER, BECAUSE THERE ARE NOW TWO CONTROLS. Until P6-183 the shell's
 * top-bar link was the only way in and built this string inline. The signed-out
 * landing page now carries the primary call as well, and two hand-rolled copies
 * of one URL contract is CLAUDE.md gotcha 21's exact shape — two writers of one
 * key, agreeing on the day they are written and drifting quietly afterwards.
 *
 * `next` is what makes the drift matter rather than merely being untidy: a
 * member who signs in from /commands should land back on /commands, and the
 * copy that stopped encoding it would still look completely correct.
 *
 * Takes the react-router location rather than reading window.location, so it is
 * a pure function a test can drive without a browser.
 */
export function signInHref({ pathname, search } = {}) {
  return `/auth/login?next=${encodeURIComponent(`${pathname ?? '/'}${search ?? ''}`)}`
}

export async function fetchSession(fetchImpl = globalThis.fetch) {
  if (typeof fetchImpl !== 'function') return { status: SIGNED_OUT, userId: null }
  try {
    const response = await fetchImpl(SESSION_ENDPOINT, {
      // The cookie is the whole point; a fetch that drops it would report every
      // signed-in member as signed out.
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
    if (!response.ok) return { status: SIGNED_OUT, userId: null }
    const body = await response.json()
    return body?.signedIn === true
      ? { status: SIGNED_IN, userId: typeof body.userId === 'string' ? body.userId : null }
      : { status: SIGNED_OUT, userId: null }
  } catch {
    return { status: SIGNED_OUT, userId: null }
  }
}

/** ⚠️ Two components call this (the shell and the trips page), so a page load
 * makes two identical requests. Deliberate, and the alternative was considered:
 * a context provider or a module-level promise cache would save one request and
 * add either a second hook name to confuse or shared mutable state to reset
 * between tests. This is the most security-sensitive code in the site and it is
 * read more often than it is run. Two cheap GETs is the cost of a file anybody
 * can audit in one sitting. If a page ever needs it a third time, reconsider. */
export function useSession() {
  const [session, setSession] = useState({ status: LOADING, userId: null })

  useEffect(() => {
    let cancelled = false
    fetchSession().then((result) => {
      if (!cancelled) setSession(result)
    })
    return () => {
      cancelled = true
    }
  }, [])

  return session
}
